import { Avatar, Col, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import styled, { css, keyframes } from 'styled-components';
import { WAVE_STATUS } from '../constants';
import { Badge, PanelHeader } from './ui';

/**
 * Participants drill-down for one wave — GET /admin/fanwave/participants. Identified by
 * its own waveId (either mode), or by gameId for a legacy Game Day run that has none, from the
 * wave-list row. Header cards from the aggregate summary, then the tap leaderboard.
 */

const PAGE_LIMIT = 50;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid ${theme.GREY_30};
  border-radius: 8px;
  background: #fff;
  color: ${theme.GREY_70};
  font-family: CeraPro;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: ${theme.SECONDARY};
    border-color: ${theme.SECONDARY};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  svg {
    ${(props) =>
      props.$spinning &&
      css`
        animation: ${spin} 0.8s linear infinite;
      `}
  }
`;

const PageButton = styled.button`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.GREY_30};
  border-radius: 8px;
  background: #fff;
  color: ${theme.GREY_70};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${theme.SECONDARY};
    border-color: ${theme.SECONDARY};
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: CeraPro;
    font-size: 12.5px;
  }

  th {
    text-align: left;
    padding: 8px 10px;
    color: ${theme.GREY_50};
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid ${theme.GREY_20};
    white-space: nowrap;
  }

  td {
    padding: 9px 10px;
    border-bottom: 1px solid ${theme.GREY_10};
    color: ${theme.GREY_80};
    white-space: nowrap;
    vertical-align: middle;
  }
`;

const SummaryCard = ({ label, value, highlight }) => (
  <Col
    noFlex
    pad="12px 14px"
    style={{
      flex: '1 1 130px',
      minWidth: '120px',
      background: highlight ? theme.SECONDARY_10 : theme.GREY_10,
      borderRadius: '11px',
      border: `1px solid ${highlight ? theme.SECONDARY_20 : theme.GREY_20}`
    }}
  >
    <Text
      marg="0"
      style={{
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: highlight ? theme.SECONDARY : theme.GREY_50
      }}
    >
      {label}
    </Text>
    <Text marg="4px 0 0" style={{ fontSize: '20px', fontWeight: 800, lineHeight: 1.1 }}>
      {value ?? 0}
    </Text>
  </Col>
);

const fmt = (d, pattern = 'MMM dd, HH:mm') => (d ? format(new Date(d), pattern) : '—');

const ParticipantsPanel = ({ visible, wave, onClose }) => {
  const [participants, setParticipants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState(null);

  // The wave key comes from the list row. Participation is keyed by the wave's OWN id in both
  // modes now — a match can hold several waves, and each keeps its own participation records —
  // so `waveId` wins wherever the row carries one. `gameId` is the fallback for a legacy Game
  // Day run whose records predate waves having identities and are still keyed by the match.
  const waveId = wave?.waveId || null;
  const gameId = !waveId && wave?.mode === 'game_day' ? wave?.gameId : null;
  const waveKey = waveId || gameId;

  const fetchParticipants = useCallback(
    async (isRefetch = false) => {
      if (!waveKey) return;
      if (isRefetch) setRefetching(true);
      else setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_LIMIT) });
      if (waveId) params.set('waveId', waveId);
      else params.set('gameId', gameId);
      const res = await Api.get(`/admin/fanwave/participants?${params.toString()}`, null, {
        silent: true
      });
      if (res?.data) {
        setParticipants(res.data.participants || []);
        setSummary(res.data.summary || null);
        setTotal(res.data.total || 0);
      } else if (res?.err) {
        setError(res.err);
      }
      if (isRefetch) setRefetching(false);
      else setLoading(false);
    },
    [waveKey, gameId, waveId, page]
  );

  useEffect(() => {
    if (visible && waveKey) fetchParticipants();
  }, [visible, waveKey, fetchParticipants]);

  // Restart from page 1 whenever a different wave opens.
  useEffect(() => {
    setPage(1);
  }, [waveKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const isLive = wave?.status === WAVE_STATUS.ACTIVE;

  return (
    <Sidepanel visible={visible} wid={900} pad="0" closeModal={onClose}>
      <PanelHeader text="FanWave participants" onClose={onClose} />
      <Col wid="100%" pad="20px 24px 40px">
        {/* Wave context + refresh */}
        <Row center between noFlex wid="100%" marg="0 0 16px" gap="12px">
          <Col noFlex style={{ minWidth: 0 }}>
            <Text b2 bold marg="0">
              {wave?.team?.name || wave?.team?.username || 'Channel'}
            </Text>
            <Text c2 col={theme.GREY_60} marg="2px 0 0">
              {wave?.mode === 'game_day'
                ? `${wave?.game?.homeName || 'TBD'} vs ${wave?.game?.awayName || 'TBD'}${
                    wave?.game?.date ? ` — ${fmt(wave.game.date, 'MMM dd, yyyy · HH:mm')}` : ''
                  }`
                : `${fmt(wave?.startsAt)} → ${fmt(wave?.endsAt)}`}
            </Text>
          </Col>
          <Row noFlex center gap="10px">
            {isLive && (
              <Badge variant="active" dot>
                Live — counts can lag ~30s
              </Badge>
            )}
            <RefreshButton
              type="button"
              $spinning={refetching ? 1 : 0}
              disabled={refetching || loading}
              onClick={() => fetchParticipants(true)}
              title="Refresh"
            >
              <FiRefreshCw size={13} />
              Refresh
            </RefreshButton>
          </Row>
        </Row>

        {/* Summary cards */}
        <Row start gap="10px" wid="100%" marg="0 0 18px" style={{ flexWrap: 'wrap' }}>
          <SummaryCard label="Participants" value={summary?.totalParticipants} highlight />
          <SummaryCard label="Total taps" value={summary?.totalTaps} />
          <SummaryCard label="Earned points" value={summary?.totalEarnedPoints} />
          <SummaryCard label="Community bonus" value={summary?.totalCommunityBonus} />
          <SummaryCard label="Rank bonus" value={summary?.totalRankBonus} />
          <SummaryCard label="Claimed" value={summary?.claimedCount} />
        </Row>

        {error && (
          <Text c1 col={theme.SECONDARY} marg="0 0 12px" style={{ fontSize: '12.5px' }}>
            {error}
          </Text>
        )}

        {loading ? (
          <Row centerAll pad="40px 0">
            <Spinner2 />
          </Row>
        ) : !participants.length ? (
          <Col
            centerAll
            noFlex
            wid="100%"
            pad="40px 0"
            style={{
              borderRadius: '12px',
              border: `1px dashed ${theme.GREY_30}`,
              background: theme.GREY_10
            }}
          >
            <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '12.5px' }}>
              No participants recorded for this wave yet.
            </Text>
          </Col>
        ) : (
          <TableWrap>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Taps</th>
                  <th>Claimed</th>
                  <th>Earned</th>
                  <th>Community bonus</th>
                  <th>Rank bonus</th>
                  <th>Claimed at</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={`${p.position}-${p.user?._id || p.user?.username}`}>
                    <td style={{ fontWeight: 700, color: theme.GREY_50 }}>{p.position}</td>
                    <td>
                      <Row noFlex center>
                        <Avatar tiny user={p.user} avatar={p.user?.avatar} />
                        <Col noFlex marg="0 0 0 8px" style={{ minWidth: 0 }}>
                          <Text b2 marg="0" style={{ fontSize: '12.5px' }}>
                            {p.user?.name || p.user?.username || 'Unknown'}
                          </Text>
                          {p.user?.username && (
                            <Text c2 col={theme.GREY_50} marg="0" style={{ fontSize: '10.5px' }}>
                              @{p.user.username}
                            </Text>
                          )}
                        </Col>
                      </Row>
                    </td>
                    <td style={{ fontWeight: 700 }}>{p.taps}</td>
                    <td style={{ color: p.claimed ? '#0C9C74' : theme.GREY_40, fontWeight: 700 }}>
                      {p.claimed ? '✓' : '–'}
                    </td>
                    <td>{p.earnedPoints}</td>
                    <td>{p.communityBonusPoints || 0}</td>
                    <td>
                      <Row noFlex center gap="6px">
                        <span>{p.rankBonusPoints || 0}</span>
                        {p.rank != null && <Badge variant="locked">#{p.rank}</Badge>}
                      </Row>
                    </td>
                    <td style={{ color: theme.GREY_60 }}>{fmt(p.claimedAt)}</td>
                    <td style={{ color: theme.GREY_60 }}>{fmt(p.joinedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}

        {/* Pagination */}
        {total > PAGE_LIMIT && (
          <Row center justifyContent="center" gap="14px" pad="14px 0 0" noFlex wid="100%">
            <PageButton type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <FiChevronLeft size={15} />
            </PageButton>
            <Text c1 col={theme.GREY_60} marg="0">
              Page {page} of {totalPages}
            </Text>
            <PageButton
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <FiChevronRight size={15} />
            </PageButton>
          </Row>
        )}
      </Col>
    </Sidepanel>
  );
};

export default ParticipantsPanel;
